import { Moment } from 'moment';
import { parse } from 'query-string';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Configuration,
  TimesheetApprovalType,
} from 'api/timesheets/configuration/configuration';
import { Avatar } from 'components/atoms/avatar';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { PageLoading } from 'components/atoms/loading';
import { Option } from 'components/atoms/select/types';
import { Page } from 'components/molecules/page';
import { UnauthorizedState } from 'components/organisms/timesheets/approval-user-view/unauthorized-view';
import { TimesheetApprovalSingleUserView } from 'components/organisms/timesheets/approval-user-view/user-view';
import { ReturnToCurrentWeek } from 'components/organisms/timesheets/common/return-to-current-week';
import { BlueGhostButton } from 'components/organisms/timesheets/common/style';
import { TitleSelect } from 'components/organisms/timesheets/common/title-select';
import { UserTitleDisplay } from 'components/organisms/timesheets/common/title-select/title-select-trigger';
import { WeekRangePicker } from 'components/organisms/timesheets/date-pickers';
import { DrawerType } from 'components/organisms/timesheets/my-timesheet';
import { WeekPreset } from 'components/organisms/timesheets/types';
import { Argon } from 'components/templates';
import { useTranslate } from 'hooks/use-translation';
import { useProjectDetailQuery } from 'queries/project';
import { useTimesheetGetRejectNote } from 'queries/timesheets/reject-note';
import { useTimesheetUrlState } from './use-timesheet-url-state';
import { useUserOptions } from './use-user-options';

const NoteLength = styled.span`
  margin-left: var(--spacing-02);
`;

const ProjectNameText = styled.div`
  color: var(--gray-70);
`;

const BlueGhostButtonStyled = styled(BlueGhostButton)`
  max-height: var(--height-base);
`;

const {
  Header: { Title },
} = Page;

const StyledTitle = styled(Title)`
  margin-top: var(--spacing-03);
  margin-bottom: var(--spacing-03);
`;

const ValidWeekPresets = [WeekPreset.ThisWeek, WeekPreset.PreviousWeek];

const WeekPresetsAllowedInURL = [WeekPreset.ThisWeek];

export const TimesheetApprovalSingleUserPage: FC<{
  configuration: Configuration;
  startDate: Moment;
  approvees: number[];
  onStartDateChange: (date: Moment) => void;
  refreshTimesheetData: () => void;
  isUserTimesheetsLoading: boolean;
}> = ({
  configuration,
  startDate,
  approvees,
  onStartDateChange,
  refreshTimesheetData,
  isUserTimesheetsLoading,
}) => {
  const translate = useTranslate();
  const { search } = useLocation();
  const values = parse(search);

  const { push } = useHistory();

  const { userId } = useParams<{ userId: string }>();

  const isProjectOwnerApproval = useMemo(
    () => configuration.approvalSetting === TimesheetApprovalType.ProjectOwner,
    [configuration.approvalSetting],
  );

  const { userOptions, users } = useUserOptions(approvees);

  const [currentSelectedUser, setCurrentSelectedUser] =
    useState<Option | undefined>();
  const [visibleDrawer, setVisibleDrawer] = useState<DrawerType>();

  const project = useProjectDetailQuery(
    ['project_id', Number(values?.projectId)],
    {
      enabled: !!values?.projectId && Number(values?.projectId) > 0,
    },
  );

  useEffect(() => {
    setCurrentSelectedUser(
      userOptions.find((option) => option.value === parseInt(userId, 10)),
    );
  }, [userId, userOptions, setCurrentSelectedUser]);

  const unAuthorized = !approvees.includes(parseInt(userId, 10));

  const {
    startDate: _startDate,
    setStartDate: _setStartDate,
    weekInUrl,
  } = useTimesheetUrlState({
    allowedWeekPresetsInUrl: WeekPresetsAllowedInURL,
    validWeekPresets: ValidWeekPresets,
    defaultStartDate: startDate,
    url: isProjectOwnerApproval
      ? `/timesheets/:week/approvals/:userId?projectId=${values?.projectId}`
      : '/timesheets/:week/approvals/:userId',
  });

  const { data: rejectNotes } = useTimesheetGetRejectNote(
    Number(userId),
    startDate.format('YYYY-MM-DD'),
  );

  const filteredRejectNotes = useMemo(() => {
    if (isProjectOwnerApproval) {
      return rejectNotes?.filter((note) => {
        if (note?.projectId === Number(values?.projectId)) {
          return note;
        }
        if (!note?.projectId && Number(values?.projectId) === -1) {
          return note;
        }

        return null;
      });
    }

    return rejectNotes;
  }, [isProjectOwnerApproval, rejectNotes, values?.projectId]);

  useEffect(() => {
    if (!_startDate.isSame(startDate, 'day')) {
      onStartDateChange(_startDate);
    }
  }, [_startDate, onStartDateChange, startDate]);

  useEffect(() => {
    return () => {
      refreshTimesheetData();
    };
  }, [refreshTimesheetData]);

  return users.length === 0 || isUserTimesheetsLoading ? (
    <PageLoading />
  ) : unAuthorized ? (
    <>
      <Helmet>
        <title>
          {translate('timesheets.title.unauthorized', {
            userName: currentSelectedUser?.label,
          })}
        </title>
      </Helmet>
      <UnauthorizedState
        goToApprovals={() => push(`/timesheets/${weekInUrl}/approvals`)}
      />
    </>
  ) : (
    <Argon
      breadcrumbs={
        <Breadcrumb.Item>
          <Link to={`/timesheets/${weekInUrl}/approvals`}>
            <FormattedMessage id="approvals" />
          </Link>
          {Number(values?.projectId) < 0 ? (
            <ProjectNameText>
              <FormattedMessage id="timesheets.labels.non_project_activity" />
            </ProjectNameText>
          ) : (
            <ProjectNameText>{project.data?.projectName}</ProjectNameText>
          )}
        </Breadcrumb.Item>
      }
      title={
        <StyledTitle>
          {!isProjectOwnerApproval ? (
            <TitleSelect
              options={userOptions}
              selected={currentSelectedUser}
              onChange={(selected) => {
                setCurrentSelectedUser(selected as Option);
                push(`/timesheets/${weekInUrl}/approvals/${selected?.value}`);
              }}
            />
          ) : (
            currentSelectedUser && (
              <UserTitleDisplay
                icon={
                  <Avatar
                    name={currentSelectedUser.label}
                    src={currentSelectedUser.imageUrl}
                    size={32}
                  />
                }
                label={currentSelectedUser.label}
                description={currentSelectedUser.description}
              />
            )
          )}
        </StyledTitle>
      }
      actions={{
        primary: (
          <>
            <WeekRangePicker value={_startDate} onChange={_setStartDate} />
            <ReturnToCurrentWeek
              currentDate={_startDate}
              onResetToDefaultWeek={_setStartDate}
            />
          </>
        ),
        secondary: (
          <>
            {filteredRejectNotes && filteredRejectNotes.length > 0 && (
              <BlueGhostButtonStyled
                onClick={() => {
                  setVisibleDrawer(DrawerType.RejectComment);
                }}
              >
                <FormattedMessage id="comments" />
                <NoteLength>({filteredRejectNotes.length})</NoteLength>
              </BlueGhostButtonStyled>
            )}
          </>
        ),
      }}
      content={
        <>
          <Helmet>
            <title>
              {translate('timesheets.title.single_users_approval', {
                userName: currentSelectedUser?.label,
              })}
            </title>
          </Helmet>
          <TimesheetApprovalSingleUserView
            startDate={_startDate}
            user={users.find((user) => user.userId === parseInt(userId, 10))!}
            configuration={configuration}
            projectId={Number(values?.projectId)}
            visibleDrawer={visibleDrawer}
            setVisibleDrawer={setVisibleDrawer}
            rejectNotes={filteredRejectNotes}
          />
        </>
      }
    />
  );
};
